import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { concat, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeWhile, tap } from 'rxjs/operators';
import { getChildRoute, TypedActivatedRoute } from '../utils/route-utils';

export interface HasTitle {
  readonly title?: TitleModel;
}
export interface TitleModel {
  componentName?: string;
  pageTitle: string;
  pageHeading?: PageHeadingModel[];
  hideRegisterSegment?: boolean;
}
export interface PageHeadingModel {
  name: string;
  route?: string;
  fragment?: string;
}
export interface ActiveRouteComponentModel {
  title?: TitleModel;
  componentName: string;
}

@Injectable({ providedIn: 'root' })
export class ActiveComponentService {
  private component$ = new Subject<HasTitle | undefined>();
  private _subscription?: Subscription;

  private _update$ = new Subject<void>();

  activeComponent$: Observable<TitleModel | undefined> = this.component$.pipe(
    switchMap(component => {
      if (!component) {
        return of(undefined);
      }

      const activatedRoute = getChildRoute(
        this.router.routerState.root as TypedActivatedRoute<{}, { title?: string }>,
      );

      return activatedRoute.data.pipe(
        switchMap(data => {
          if (data.title) {
            return of({ pageTitle: data.title });
          }

          return concat(
            // give time for component to react to route data
            timer(1, 500).pipe(
              map(() => component.title),
              distinctUntilChanged(),
              // Does title not exist or end in an ellipsis '…' ?
              takeWhile(
                title => !title || !title.pageTitle || /\u2026$/.test(title.pageTitle),
                /* inclusive (includes first non-match) */ true,
              ),
              tap(title => {
                if (!title) {
                  environment.warn(
                    `No title for current page\nURL: ${location.href}\nComponent: ${component.constructor.name}`,
                  );
                }
              }),
              startWith(undefined),
            ),
            // Also respond to updates
            this._update$.pipe(
              map(() => component.title),
              distinctUntilChanged(),
            ),
          );
        }),
      );
    }),
  );

  title$: Observable<TitleModel | undefined> = this.activeComponent$;

  constructor(private router: Router) {}

  init() {
    this._subscription = this.title$.subscribe(t => this.updateWindowTitle(t?.pageTitle));
  }

  destroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = undefined;
    }
  }

  refresh() {
    this._update$.next();
  }

  updateWindowTitle(title?: string) {
    document.title = title ? `${title} - FinQuery Fixed Assets` : 'FinQuery Fixed Assets';
  }

  onActivate($event: any) {
    this.component$.next($event);
  }

  onDeactivate() {
    this.component$.next(undefined);
  }
}
